import useStore from '@/common/hooks/useStore';
import Notifier from '@/common/services/Notifier';
import Storage, { KEY_DEADZONES_PENDING_TASKS, KEY_SPECTRUM_PENDING_TASKS } from '@/common/services/Storage';
import SpectrumAPI, { SpectrumModelsType } from '@/spectrum/api/SpectrumAPI';
import { generateSuccessMessage } from '@/spectrum/helpers/generateTaskMessage';
import { PendingTasksService } from '@/spectrum/services/PendingTasksService';

const LISTENER_TIMEOUT = 5000;

const PENDING_TASK_MODELS_TYPE_MAP: Record<string, SpectrumModelsType> = {
  [KEY_DEADZONES_PENDING_TASKS]: 'deadzones',
  [KEY_SPECTRUM_PENDING_TASKS]: 'spectrum',
};

export default class PendingSpectrumTasksStore {
  private listenerTimeout?: SetTimeout;

  private spectrumAPI: SpectrumAPI;

  constructor() {
    this.spectrumAPI = new SpectrumAPI();
  }

  listen() {
    this.listener();
  }

  ignore() {
    clearTimeout(this.listenerTimeout);
  }

  private listener = () => {
    // listener is always running and checking if there are any pending task in local storage
    clearTimeout(this.listenerTimeout);
    this.listenerTimeout = setTimeout(() => {
      const pendingTasksByGroup = PendingTasksService.pendingTasksByGroup();
      const groups = Object.keys(pendingTasksByGroup);

      // check that "localStorage" has pending tasks,
      // if not - rerun listener, user might add tasks later
      if (groups.length === 0) {
        this.listener();

        return;
      }

      Promise.all(groups.map((group) => this.notifyCompletedGroupTasks(group, pendingTasksByGroup[group]))).finally(
        () => this.listener(),
      );
    }, LISTENER_TIMEOUT);
  };

  private notifyCompletedGroupTasks = (group: string, taskIds: string[] = []) => {
    return this.spectrumAPI
      .fetchModels(PENDING_TASK_MODELS_TYPE_MAP[group])
      .then((response) => {
        taskIds.forEach((taskId) => {
          const model = response.content.find(({ jobId }) => jobId === taskId);

          if (model && !model.demo && model.model && model.productId) {
            Notifier.success(generateSuccessMessage(group, taskId, model), {
              // always show until user make some action
              delay: 0,
              // defining "toastId" will prevent show notification again
              toastId: `${group}-${taskId}-${model.productId}`,
              isClosedOnClick: false,
              onRemove: () => Storage.pullout(group, taskId, Storage.STORAGE_LOCAL),
            });
          }
        });
      })
      .catch(() => {
        // do nothing
      });
  };
}

export const usePendingSpectrumTasksStore = () => useStore(PendingSpectrumTasksStore);

export const pendingSpectrumTasksStore = new PendingSpectrumTasksStore();
